import React, { useState, useEffect } from "react";
import {
  useCommittees,
  useCommitteeStatus,
  useCreateCommittee,
  useEditCommittee,
} from "../hooks/useCommittees";

import { Dashboard } from "../../DashboardPage/useDashboard";
import AddItemButton from "ManagementTeam/Shared/AddItemButton";
import { LoadingCommitteeHeader } from "../CommitteeHeader/CommitteeHeader";
import AddCommitteeModal from "../AddCommitteeModal";

import styles from "./Committees.module.css";
import CommitteesItem from "../CommitteesItem";

export function Committees({ readOnly }) {
  const { data: dashboard, refetchDashboard } = Dashboard.useContainer();

  // Ensure dashboard data is fetched on initial mount (container defaults to inactive)
  useEffect(() => {
    refetchDashboard && refetchDashboard();
  }, []);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCommittee, setEditingCommittee] = useState({
    name: undefined,
    description: undefined,
    yearStarted: undefined,
    status: "Current",
    members: [],
  });

  const {
    data: committees,
    isLoading,
    isError,
    error,
  } = useCommittees("current");

  const archiveCommittee = useCommitteeStatus("Archived");
  const createCommittee = useCreateCommittee();
  const editCommittee = useEditCommittee();

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleSubmitAddModal = (form, members) => {
    let committee = {
      name: form.name,
      description: form.description,
      yearStarted: form.yearStarted,
      status: "Current",
      members: [...members],
    };
    createCommittee(committee);
    setShowAddModal(false);
  };

  const handleArchiveCommittee = (id) => {
    archiveCommittee(id);
  };

  const handleEditCommittee = (id) => {
    setShowEditModal(true);
    const index = committees.findIndex((committee) => committee._id === id);
    const editing = {
      ...committees[index],
      _id: committees[index]._id,
    };
    setEditingCommittee({ ...editing });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setEditingCommittee({
      title: "",
      description: "",
      year: "",
      members: [],
    });
    setShowEditModal(false);
  };

  const handleSaveEditModal = (form, members) => {
    const index = committees.findIndex(
      (committee) => committee._id === editingCommittee._id
    );
    const committee = {
      committeeId: committees[index]._id,
      ...form,
      members: [...members],
    };
    editCommittee(committee);
    setShowEditModal(false);
    refetchDashboard();
  };

  if (isError) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <>
      <div className={styles.addModal}>
        {showAddModal && (
          <AddCommitteeModal
            isOpen={true}
            onClose={() => handleCloseAddModal()}
            onSave={handleSubmitAddModal}
          />
        )}
        {showEditModal && editingCommittee && (
          <AddCommitteeModal
            isOpen={true}
            committeeMember={editingCommittee}
            onClose={() => handleCloseEditModal()}
            onSave={handleSaveEditModal}
          />
        )}
      </div>
      <div
        style={{ visibility: !readOnly ? "visible" : "hidden" }}
        className={styles.subHeader}
      >
        <div className={styles.addButton}>
          <AddItemButton
            title="ADD NEW COMMITTEE"
            onClick={() => setShowAddModal(true)}
          />
        </div>
      </div>

      {isLoading || !dashboard || !dashboard.users ? (
        <div className={styles.committeeContainer}>
          <LoadingCommitteeHeader />
          <LoadingCommitteeHeader />
          <LoadingCommitteeHeader />
          <LoadingCommitteeHeader />
        </div>
      ) : committees.length === 0 ? (
        <div className={styles.loading}>No Committees Found</div>
      ) : (
        committees &&
        committees.map(
          (committee, index) =>
            committee.status === "Current" && (
              <CommitteesItem
                key={index}
                readOnly={readOnly}
                committee={committee}
                onArchive={() => handleArchiveCommittee(committee._id)}
                onEdit={() => handleEditCommittee(committee._id)}
                onUpdateCompleted={() => refetchDashboard()}
                onSwapCompleted={() => refetchDashboard()}
              />
            )
        )
      )}
    </>
  );
}
